import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import styled, { ThemeProvider, useTheme } from "styled-components";
import useAbacExt from "~/hooks/abac";
import Markdown from "~/components/Markdown";
import { Avatar, Button, Grid, Icon, Modal, Text } from "~/components-shared";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import InputMarkdownTextAreaControl from "~/components/InputMarkdownTextAreaControl";
import toastsActionCreators from "~/store/toasts/actions";
import { AuthoritiesEnum } from "~/types/enums";
import { object, string } from "yup";
import TopicNoteDeleteModal from "~/components/TopicNoteDeleteModal";
import TopicNoteShareModal from "~/components/TopicNoteShareModal";
import lists from "~/lib/lists";
import topicFootprintActionCreators from "~/store/topicFootprints/actions";
import themes from "~/lib/theme/theme";
const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: start;
  margin-bottom: 1rem;
`;
const Author = styled(Text)`
  display: flex;
  align-items: center;
`;
const StyledAvatar = styled(Avatar)`
  box-shadow: rgb(255, 255, 255) 0 0 0 2px, rgb(253, 232, 99) 0 0 0 4px;
  cursor: default;
`;
const Form = styled.form`
  min-width: 480px;
`;
const Note = styled.div`
  position: relative;
  display: inline-block;
  border-left: 3px solid ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.note.main;
}};
  background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.note.secondary;
}};
  color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.note.text;
}};
  border-radius: 3px;
  padding: 0.5rem 1rem;
  box-shadow: rgba(0, 0, 0, 0.04) 3px 3px 3px;
  margin-top: 0.5rem;

  &:hover {
    cursor: text;
  }
`;
const TopicFootprintNotes = _ref4 => {
  let {
    topicId,
    footprint
  } = _ref4;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const [editId, setEditId] = useState();
  const [deleteId, setDeleteId] = useState();
  const [shareId, setShareId] = useState();
  const {
    userHas
  } = useAbacExt();
  const defaultValues = {
    text: ""
  };
  const {
    notes
  } = footprint;
  const {
    handleSubmit,
    control,
    setValue
  } = useForm({
    resolver: yupResolver(object().shape({
      text: string().required()
    })),
    defaultValues
  });
  if (!notes.length) {
    notes.push({
      id: "+",
      text: "Add a note",
      memberAccessibility: [],
      isOwner: true,
      canEdit: true,
      canView: true
    });
  }
  const handleEditNote = (id, text) => {
    if (!editId && userHas(AuthoritiesEnum.TOPICS_WRITE)) {
      setValue("text", text);
      setEditId(id);
    }
  };
  const handleCancelEditNote = () => {
    setEditId(undefined);
    setValue("text", "");
  };
  const onSubmit = handleSubmit(data => {
    if (editId === "+") {
      dispatch(topicFootprintActionCreators.createNoteStarted(topicId, footprint.id, data, _ref5 => {
        let {
          data: note
        } = _ref5;
        if (note) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("TopicFootprintNotes.noteCreated"),
            timeout: 10000
          }));
          setEditId(undefined);
        }
      }));
    } else if (editId) {
      dispatch(topicFootprintActionCreators.updateNoteStarted(topicId, footprint.id, editId, data, _ref6 => {
        let {
          data: note
        } = _ref6;
        if (note) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("TopicFootprintNotes.noteUpdated"),
            timeout: 10000
          }));
          setEditId(undefined);
        }
      }));
    }
  });
  return /*#__PURE__*/React.createElement(Wrapper, null, notes.map(note => /*#__PURE__*/React.createElement(Note, {
    key: note.id,
    onClick: () => note.canEdit && handleEditNote(note.id, note.text)
  }, editId === note.id ? /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: themes.light
  }, /*#__PURE__*/React.createElement(InputMarkdownTextAreaControl, {
    color: "transparent",
    name: "text",
    control: control
  }), /*#__PURE__*/React.createElement(Grid, {
    cols: note.isOwner ? 2 : 1,
    justify: "space-between"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Button, {
    onClick: onSubmit,
    color: "primary",
    s: {
      px: 4,
      py: 0.5,
      mr: 1
    },
    fontSize: 3,
    display: "inline-block",
    rounded: true
  }, t("common.actions.save")), /*#__PURE__*/React.createElement(Button, {
    onClick: handleCancelEditNote,
    color: "transparent",
    s: {
      px: 3,
      py: 0.5
    },
    fontSize: 3,
    display: "inline-block",
    rounded: true
  }, t("common.actions.cancel"))), note.isOwner && note.id !== "+" && /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 3,
    color: "textSecondary",
    weight: "semiBold",
    s: {
      mr: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "visibility",
    scale: 3,
    s: {
      mr: 1,
      mb: 0.5
    }
  }), note.memberAccessibility.length ? t("TopicFootprintNotes.visibleFor", {
    count: note.memberAccessibility.length
  }) : t("TopicFootprintNotes.visibleForYou")), /*#__PURE__*/React.createElement(Button, {
    onClick: () => setShareId(note.id),
    color: "primaryTransparent",
    s: {
      px: 3,
      py: 0.5
    },
    fontSize: 3,
    display: "inline-block",
    rounded: true
  }, t("common.actions.share")), /*#__PURE__*/React.createElement(Button, {
    onClick: () => setDeleteId(note.id),
    color: "dangerTransparent",
    s: {
      px: 3,
      py: 0.5
    },
    fontSize: 3,
    display: "inline-block",
    rounded: true
  }, t("common.actions.delete")))))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Markdown, {
    text: note.text
  }), (!note.isOwner || !!note.memberAccessibility.length) && /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 3,
    template: "1fr 1rem",
    align: "end"
  }, /*#__PURE__*/React.createElement(Author, {
    fontSize: 3,
    s: {
      mt: 2
    }
  }, /*#__PURE__*/React.createElement(StyledAvatar, {
    size: 6,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": `${note.createdBy.firstName} ${note.createdBy.lastName} ${t("TopicFootprintNotes.isOwner")}`,
    s: {
      mr: 2
    },
    user: note.createdBy
  }), note.memberAccessibility.map(_ref7 => {
    let {
      member,
      accessType
    } = _ref7;
    return /*#__PURE__*/React.createElement(Avatar, {
      key: member.id,
      size: 6,
      s: {
        mr: 1
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": `${member.firstName} ${member.lastName} ${t("TopicFootprintNotes.can")} ${lists.topics.footprintNoteAccess.names[accessType]}`,
      user: member,
      style: {
        cursor: "default"
      }
    });
  })), /*#__PURE__*/React.createElement(Icon, {
    scale: 3.5,
    src: note.canEdit ? "lockOpen" : "lock",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": note.canEdit ? t("TopicFootprintNotes.youCanEdit") : t("TopicFootprintNotes.youCanNotEdit"),
    color: colors.text.secondary,
    s: {
      mb: 1
    }
  }))))), /*#__PURE__*/React.createElement(Modal, {
    open: !!deleteId
  }, /*#__PURE__*/React.createElement(TopicNoteDeleteModal, {
    noteId: deleteId,
    footprintId: footprint.id,
    topicId: topicId,
    onClose: () => setDeleteId(undefined)
  })), /*#__PURE__*/React.createElement(Modal, {
    open: !!shareId
  }, /*#__PURE__*/React.createElement(TopicNoteShareModal, {
    note: notes.find(_ref8 => {
      let {
        id
      } = _ref8;
      return id === shareId;
    }),
    footprintId: footprint.id,
    topicId: topicId,
    onClose: () => setShareId(undefined)
  })));
};
export default TopicFootprintNotes;